import React from "react";
import classnames from "classnames";
import { isBoosterPlan, Plan, roundTo, toCurrency } from "@ecologi/shared";
import TreesIcon from "@ecologi/assets/icons/Plan/trees.svg";
import CarbonIcon from "@ecologi/assets/icons/Plan/carbon.svg";
import LeafTickIcon from "@ecologi/assets/icons/Plan/leaf_tick_shadow.svg";
import WhiteTickIcon from "@ecologi/assets/icons/Plan/white_tick.svg";
import { getFootprintFraction } from "./Plan";

import styles from "../styles/PlanCard.module.scss";
import { Typography } from "../../Typography";

type Props = {
  currencySymbol: string;
  isSelected: boolean;
  plan: Plan;
  selectPlan: (planId: string) => void;
};

export const PlanCard = ({
  currencySymbol,
  isSelected,
  plan,
  selectPlan,
}: Props) => {
  const cls = classnames(styles.planCard, {
    [styles.planCardSelected]: isSelected,
  });
  const showLeafIcon = isBoosterPlan(plan.id);

  return (
    <div
      className={cls}
      data-testid={plan.title.replace(" ", "-").concat("-plancard")}
    >
      <div className={styles.planCardHeader}>
        <div className={styles.planCardHeaderTop}>
          <div>
            <h3 className={styles.planCardHeaderText}>{plan.title}</h3>
            <p className={styles.planCardHeaderAmount}>
              {currencySymbol === "$" ? "US$" : currencySymbol}
              {toCurrency(plan.price)}/month
            </p>
          </div>
          {showLeafIcon && <LeafTickIcon />}
        </div>
        <p className={styles.planCardDescription}>{plan.description}</p>
      </div>
      <div className={styles.planCardDetails}>
        <div className={styles.planCardTrees}>
          <TreesIcon className={styles.planCardIcon} />
          <div>
            <p className={styles.planCardNumberOf}>
              <Typography use="inlineLink" as="a" href="/projects">
                {plan.stats.trees} trees funded
              </Typography>
            </p>
            <p className={styles.planCardEvery}>every month</p>
          </div>
        </div>
        <div className={styles.planCardCarbon}>
          <CarbonIcon className={styles.planCardIcon} />
          <div>
            <p className={styles.planCardNumberOf}>
              {plan.stats.carbonAnnual || roundTo(2)(plan.stats.carbon * 12)}{" "}
              tonnes CO2
            </p>
            <p className={styles.planCardEvery}>
              avoided every year
              <div>
                (~{getFootprintFraction(plan.stats.footprints)}x footprint*)
              </div>
            </p>
          </div>
        </div>
        <button
          className={styles.planCardButton}
          disabled={isSelected}
          onClick={(e) => {
            e.preventDefault();
            e.currentTarget.blur();
            selectPlan(plan.id);
          }}
          type="button"
          data-testid="planCard-button"
        >
          {isSelected && <WhiteTickIcon />}
          {isSelected ? "Selected" : "Choose plan"}
        </button>
      </div>
    </div>
  );
};
